import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import debounce from 'lodash-es/debounce'
import React, { useCallback, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { useGlobalContext } from '../../Contexts/GlobalContext'
import { forgotPassword, isEmailTaken } from '../../globalHeader/functions/ams-functions'
import BaseDialog from '../Common/BaseDialog'
import ErrorBlock from '../Common/ErrorBlock'
import { ValidationState } from '../Common/types'
import UserIcon from '../Icons/user'

const ForgotPassword: React.FC = () => {
    const { env, onAuthClose } = useGlobalContext()
    const { unregisterDialog } = useDialogs()
    const [email, setEmail] = useState('')
    const [isEmailValid, setIsEmailValid] = useState(false)
    const [emailCheckState, setEmailCheckState] = useState(ValidationState.WAITING)
    const [errorMessage, setErrorMessage] = useState('')

    const debouncedEmailCheck = useCallback(
        debounce(async (email) => {
            const result = await isEmailTaken(email)
            setEmailCheckState(result ? ValidationState.TAKEN : ValidationState.FREE)
        }, 1000),
        [],
    )

    const onEmailChange = async (email: string) => {
        const current = email
        setEmail(current)
        const regex =
            env === 'prod'
                ? /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                : /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        if (current.length < 3 || !regex.test(current)) {
            setIsEmailValid(false)
            return
        }
        setEmailCheckState(ValidationState.LOADING)
        setIsEmailValid(true)
        await debouncedEmailCheck(current)
    }

    const submitHandler = async (event: any) => {
        event.preventDefault()
        if (isEmailValid) {
            const result = await forgotPassword(email)
            if (!result.error) {
                // TODO: dialog instead of alert
                alert('An email has been sent to reset your password!')
                unregisterDialog(Dialogs.FORGOT_PASSWORD)
            } else {
                setErrorMessage(result.error.message)
            }
        }
    }

    return (
        <BaseDialog name={Dialogs.FORGOT_PASSWORD} title={'Forgot password?'} onClose={() => onAuthClose()} forceBlock>
            {errorMessage && <ErrorBlock error={errorMessage} />}
            <TextField
                label="Enter your email:"
                margin="normal"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                            <IconButton edge="end" disabled>
                                <UserIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                error={
                    (!isEmailValid && isEmailValid !== undefined && email.length > 3) ||
                    emailCheckState === ValidationState.FREE
                }
                helperText={
                    !isEmailValid && isEmailValid !== undefined && email.length > 3
                        ? 'E-mail not valid'
                        : emailCheckState === ValidationState.FREE
                          ? 'E-mail not registered. Please sign up.'
                          : undefined
                }
            />
            <Button
                size="large"
                fullWidth
                onClick={submitHandler}
                disabled={!isEmailValid || emailCheckState !== ValidationState.TAKEN}
            >
                Request Reset
            </Button>
        </BaseDialog>
    )
}

export default ForgotPassword
